import api from 'api'
import { AxiosError, isCancel } from 'axios'
import { useLocalStorageState } from 'hooks/useStorage'
import { useEffect, useState } from 'react'

import { CHAMADA_PAINEL_STORAGE_KEYS, CHAMADA_PAINEL_TIMERS } from '../constants-chamadaPainel'
import { GerarPinPareamentoResponse, StatusPareamentoDispositivoPainelEnum } from '../model-chamadaPainel'

export interface UsePareamentoPainelResult {
  pinUsuario?: string
  isGenerating: boolean
  hasFatalError: boolean
  isUnauthorizedUser: boolean
}

export function usePainelPareamento(
  onPareado: (tokenAcesso: string, uuidDispositivo: string) => void
): UsePareamentoPainelResult {
  const [
    dadosPareamento,
    setDadosPareamento,
    removeDadosPareamento,
  ] = useLocalStorageState<GerarPinPareamentoResponse | null>(CHAMADA_PAINEL_STORAGE_KEYS.DADOS_PAREAMENTO)

  const [hasFatalError, setHasFatalError] = useState(false)
  const [isGeneratingPin, setIsGeneratingPin] = useState(false)
  const [isUnauthorizedUser, setIsUnauthorizedUser] = useState(false)

  useEffect(() => {
    if (dadosPareamento?.uuidDispositivo || hasFatalError || isUnauthorizedUser) return

    const controller = new AbortController()

    const gerarCodigo = async () => {
      setIsGeneratingPin(true)
      try {
        const response = await api.chamadaPainel.gerarPin({ signal: controller.signal })
        const data = response.data

        setDadosPareamento({
          pinUsuario: data.pinUsuario,
          uuidDispositivo: data.uuidDispositivo,
        })
        setHasFatalError(false)
        setIsGeneratingPin(false)
      } catch (error) {
        if (!isCancel(error)) {
          const axiosError = error as AxiosError
          if (axiosError.isAxiosError && axiosError.response?.status === 401) {
            setIsUnauthorizedUser(true)
          } else {
            setHasFatalError(true)
          }
          setIsGeneratingPin(false)
        }
      }
    }

    gerarCodigo()

    return () => {
      controller.abort()
    }
  }, [dadosPareamento?.uuidDispositivo, hasFatalError, isUnauthorizedUser, setDadosPareamento])

  useEffect(() => {
    if (!dadosPareamento?.uuidDispositivo || isUnauthorizedUser || hasFatalError) return

    const controller = new AbortController()
    let timeoutId: NodeJS.Timeout

    const checkStatus = async () => {
      try {
        const response = await api.chamadaPainel.verificarStatusPareamento(dadosPareamento.uuidDispositivo, {
          signal: controller.signal,
        })
        const data = response.data

        if (data.status === StatusPareamentoDispositivoPainelEnum.PAREADO && data.tokenAcesso) {
          removeDadosPareamento(false)
          onPareado(data.tokenAcesso, dadosPareamento.uuidDispositivo)
        } else if (data.status === StatusPareamentoDispositivoPainelEnum.INVALIDO) {
          removeDadosPareamento()
        } else {
          timeoutId = setTimeout(checkStatus, CHAMADA_PAINEL_TIMERS.POLLING_PAREAMENTO)
        }
      } catch (error) {
        if (!isCancel(error)) {
          const axiosError = error as AxiosError
          if (axiosError.isAxiosError && axiosError.response?.status === 401) {
            setIsUnauthorizedUser(true)
          } else {
            setHasFatalError(true)
          }
        }
      }
    }

    checkStatus()

    return () => {
      controller.abort()
      if (timeoutId) clearTimeout(timeoutId)
    }
  }, [dadosPareamento?.uuidDispositivo, hasFatalError, isUnauthorizedUser, onPareado, removeDadosPareamento])

  const isGenerating = isGeneratingPin || (!dadosPareamento?.pinUsuario && !hasFatalError && !isUnauthorizedUser)

  return {
    pinUsuario: dadosPareamento?.pinUsuario,
    isGenerating,
    hasFatalError,
    isUnauthorizedUser,
  }
}
